// ABOUTME: Editor modal for warehouses to write a news message for their relations.
// ABOUTME: Label-left rows (warehouses, relations all/specific, title, tiptap content) with save-draft / save-and-publish footer.

import { Box, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import PublishIcon from "@mui/icons-material/Publish";

import { tokens } from "../../../../theme";
import Text from "../../../theme/text/Text";
import Subheading from "../../../theme/text/Subheading";
import SHModal from "../../../theme/Modal/SHModal";
import ButtonGroup from "../../../theme/buttons/ButtonGroup";
import Shbutton from "../../../theme/buttons/Shbutton";
import ShValidatedInput from "../../../theme/inputs/ShValidatedInput";
import { InputDropdown } from "../../../theme/dropdowns/InputDropdown";
import ShPillToggle from "../../../theme/buttons/ShPillToggle";
import ChatInput from "../../../theme/chat/ChatInput";
import {
    AUDIENCE_ALL,
    AUDIENCE_SPECIFIC,
    audienceFromNewsItem,
    buildRelationNewsPayload,
    canPublish,
    pruneRelationIds,
    relationsForWarehouses,
} from "./relationNewsPayload";
import {
    useAddRelationNewsMutation,
    useGetRelationNewsQuery,
    usePublishRelationNewsMutation,
    useUpdateRelationNewsMutation,
} from "../../../../newapi/warehouse/relationNewsSlice";
import { useGetWarehousesSelectQuery } from "../../../../newapi/warehouse/warehouseApiSlice";
import { useGetRelationsSelectQuery } from "../../../../newapi/global/relation/relationSlice";
import { showToastMessage, showToastMessageRequestError } from "../../../../utils/toasts";

const AUDIENCE_OPTIONS = [
    { value: AUDIENCE_ALL, label: "all_relations" },
    { value: AUDIENCE_SPECIFIC, label: "specific_relations" },
];

const MCreateRelationNews = ({ open, handleClose, newsId, onDelete }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [title, setTitle] = useState("");
    const [body, setBody] = useState(null);
    const [warehouseIds, setWarehouseIds] = useState([]);
    const [audience, setAudience] = useState(AUDIENCE_ALL);
    const [relationIds, setRelationIds] = useState([]);
    const [saving, setSaving] = useState(false);

    const { data: existing } = useGetRelationNewsQuery(newsId, { skip: !newsId || !open });
    const { data: warehouses } = useGetWarehousesSelectQuery();
    const { data: relations } = useGetRelationsSelectQuery(undefined, { skip: !open });

    const [addRelationNews] = useAddRelationNewsMutation();
    const [updateRelationNews] = useUpdateRelationNewsMutation();
    const [publishRelationNews] = usePublishRelationNewsMutation();

    // Hydrate once per opened message. A cache refetch while the modal is open hands back a new
    // `existing` object; without the guard that would wipe whatever the user has typed since.
    const hydratedForRef = useRef(null);
    useEffect(() => {
        if (existing && open && hydratedForRef.current !== newsId) {
            hydratedForRef.current = newsId;
            setTitle(existing.title ?? "");
            setBody(existing.body ?? null);
            setWarehouseIds((existing.warehouses ?? []).map((w) => w.id));
            setAudience(audienceFromNewsItem(existing));
            setRelationIds((existing.relations ?? []).map((r) => r.id));
        }
    }, [existing, open, newsId]);

    // Reset when the modal closes.
    useEffect(() => {
        if (!open) {
            setTitle("");
            setBody(null);
            setWarehouseIds([]);
            setAudience(AUDIENCE_ALL);
            setRelationIds([]);
            setSaving(false);
            hydratedForRef.current = null;
        }
    }, [open]);

    const relationOptions = useMemo(
        () => relationsForWarehouses(relations, warehouseIds),
        [relations, warehouseIds]
    );

    const handleWarehousesChange = (selected) => {
        setWarehouseIds(selected);
        setRelationIds((prev) => pruneRelationIds(prev, relations, selected));
    };

    const handleBodyChange = useCallback((data) => {
        setBody(data?.content ?? null);
    }, []);

    const persist = async () => {
        const payload = buildRelationNewsPayload({ title, body, warehouseIds, audience, relationIds });
        if (newsId) {
            return await updateRelationNews({ id: newsId, ...payload }).unwrap();
        }
        return await addRelationNews(payload).unwrap();
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await persist();
            showToastMessage("success", t("saved") || "Opgeslagen");
            handleClose();
        } catch (err) {
            showToastMessageRequestError(t("save_failed") || "Opslaan mislukt", t, err);
        } finally {
            setSaving(false);
        }
    };

    const handlePublish = async () => {
        setSaving(true);
        try {
            const saved = await persist();
            await publishRelationNews(saved.id ?? newsId).unwrap();
            showToastMessage("success", t("published") || "Gepubliceerd");
            handleClose();
        } catch (err) {
            showToastMessageRequestError(t("save_failed") || "Opslaan mislukt", t, err);
        } finally {
            setSaving(false);
        }
    };

    const publishDisabled = saving || !canPublish({ title, body, warehouseIds, audience, relationIds });
    const editorReady = !newsId || Boolean(existing);

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    return (
        <SHModal
            open={open}
            onClose={handleClose}
            sx={{ body: { minWidth: 720, maxWidth: 720 } }}
        >
            <Box
                sx={{
                    textAlign: "center",
                    paddingY: 4,
                    paddingX: 5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                <Subheading>{t("new_news_message") || "Nieuw bericht"}</Subheading>
            </Box>

            {/* BODY */}
            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding: 3,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                {/* Warehouses */}
                <Box sx={{ width: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 7 }}>
                    <Box sx={{ width: 120, height: 32, display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <Text light>{t("warehouses")}</Text>
                    </Box>
                    <Box sx={{ flex: 1, borderRadius: 4, display: "flex" }}>
                        <InputDropdown
                            displayName="name"
                            valueKey="id"
                            noSelection={t("choose")}
                            options={warehouses?.data ?? []}
                            selected={warehouseIds}
                            multiple
                            onChange={handleWarehousesChange}
                            closeOnSelection={false}
                        />
                    </Box>
                </Box>

                {/* Relations: everyone in the chosen warehouses, or a hand-picked set */}
                <Box sx={{ width: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 7 }}>
                    <Box sx={{ width: 120, height: 32, display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <Text light>{t("relations")}</Text>
                    </Box>
                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                        <Box sx={{ display: "flex" }}>
                            <ShPillToggle
                                options={AUDIENCE_OPTIONS}
                                value={audience}
                                onChange={setAudience}
                            />
                        </Box>
                        {audience === AUDIENCE_SPECIFIC && (
                            <Box sx={{ borderRadius: 4, display: "flex" }}>
                                <InputDropdown
                                    displayName="name"
                                    valueKey="id"
                                    noSelection={t("choose")}
                                    options={relationOptions}
                                    selected={relationIds}
                                    multiple
                                    onChange={(selected) => {
                                        setRelationIds(selected);
                                    }}
                                    closeOnSelection={false}
                                    searchOptions={{
                                        enabled: true,
                                        placeholder: t("search"),
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Title */}
                <Box sx={{ width: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 7 }}>
                    <Box sx={{ width: 120, height: 32, display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <Text light>{t("title")}</Text>
                    </Box>
                    <Box sx={{ flex: 1, borderRadius: 4, display: "flex" }}>
                        <ShValidatedInput
                            name="title"
                            value={title}
                            onChange={(key, value) => setTitle(value)}
                        />
                    </Box>
                </Box>

                {/* Content - rich text */}
                <Box sx={{ width: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 7 }}>
                    <Box sx={{ width: 120, height: 32, display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <Text light>{t("content")}</Text>
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            borderRadius: 4,
                            background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                            p: 2,
                            minHeight: 160,
                            display: "flex",
                            flexDirection: "column",
                            pb: 1,
                            px: 1,
                        }}
                    >
                        {editorReady && (
                            <ChatInput
                                hideSend
                                hideAttachments
                                // Read straight from the saved message: tiptap only takes content at
                                // creation, and the editor mounts before `body` is hydrated.
                                initialContent={existing?.body}
                                onDataChange={handleBodyChange}
                                colors={colors}
                                theme={theme}
                                placeholder={t("write_something") || "Schrijf iets..."}
                            />
                        )}
                    </Box>
                </Box>
            </Box>

            <Box
                component="footer"
                sx={{
                    display: "flex",
                    width: 1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingRight: 3,
                    zIndex: 1058,
                    position: "relative",
                    paddingTop: 2,
                    background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                    "@media screen and (max-width: 47.9375em)": {
                        paddingRight: 0,
                    },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Shbutton
                        isCancel={true}
                        className={"always-visible"}
                        onClick={handleClose}
                        color="primary"
                        variant="text"
                        startIcon={<ArrowRightAltIcon />}
                    >
                        {t("cancel")}
                    </Shbutton>

                    {onDelete && newsId && (
                        <Shbutton
                            className={"always-visible"}
                            onClick={() => onDelete(newsId)}
                            color="red"
                            variant="contained"
                        >
                            {t("delete")}
                        </Shbutton>
                    )}
                </Box>

                <ButtonGroup version={2}>
                    <Shbutton
                        className={"group-btn"}
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving}
                        startIcon={<SaveOutlinedIcon />}
                    >
                        {t("save")}
                    </Shbutton>
                    <Shbutton
                        className={"group-btn"}
                        color="green"
                        variant="contained"
                        onClick={handlePublish}
                        disabled={publishDisabled}
                        startIcon={<PublishIcon />}
                    >
                        {t("save_and_publish") || "Opslaan & publiceren"}
                    </Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    );
};

export default MCreateRelationNews;
